import React, { useMemo, useState } from 'react';
import { Platform, Pressable, StyleSheet, TextInput, View } from 'react-native';
import { BottomSheetTextInput } from '@gorhom/bottom-sheet';
import Feather from '@react-native-vector-icons/feather/static';
import { GTFonts, useTheme } from '../../theme';
import { borderWidth, fs, iconSize, s, v } from '../../utils/libs';

interface PickerSearchBarProps {
  value: string;
  onChangeText: (text: string) => void;
  placeholder: string;
  onSubmit?: () => void;
  /** Shows the square + button after the field. */
  onAddNew?: () => void;
  inputRef?: React.RefObject<TextInput | null>;
  autoFocus?: boolean;
}

/** The picker's search field, with × to clear and an optional + beside it. */
const PickerSearchBar: React.FC<PickerSearchBarProps> = ({
  value,
  onChangeText,
  placeholder,
  onSubmit,
  onAddNew,
  inputRef,
  autoFocus = false,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [focused, setFocused] = useState(false);
  const active = focused || value.length > 0;

  return (
    <View style={styles.row}>
      <View style={[styles.field, active && styles.fieldActive]}>
        <Feather
          name="search"
          size={iconSize(18)}
          color={active ? theme.colors.primaryDark : theme.colors.textGray500}
        />
        <BottomSheetTextInput
          ref={inputRef as any}
          value={value}
          onChangeText={onChangeText}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onSubmitEditing={onSubmit}
          placeholder={placeholder}
          placeholderTextColor={theme.colors.textGray500}
          accessibilityLabel={placeholder}
          style={[styles.input, active && styles.inputActive]}
          returnKeyType="done"
          autoCapitalize="words"
          autoCorrect={false}
          autoFocus={autoFocus}
        />
        {value.length > 0 ? (
          <Pressable
            onPress={() => onChangeText('')}
            accessibilityRole="button"
            accessibilityLabel="Clear search"
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            style={styles.clear}>
            <Feather
              name="x"
              size={iconSize(12)}
              color={theme.colors.textGray600}
            />
          </Pressable>
        ) : null}
      </View>
      {onAddNew ? (
        <Pressable
          onPress={onAddNew}
          accessibilityRole="button"
          accessibilityLabel="Add new customer"
          style={({ pressed }) => [styles.add, pressed && styles.addPressed]}>
          <Feather
            name="user-plus"
            size={iconSize(20)}
            color={theme.colors.primaryDark}
          />
        </Pressable>
      ) : null}
    </View>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    row: {
      flexDirection: 'row',
      gap: s(10),
    },
    field: {
      flex: 1,
      minWidth: 0,
      height: v(48),
      borderRadius: s(14),
      backgroundColor: theme.colors.surfaceSecondary,
      borderWidth: borderWidth(1.5),
      borderColor: 'transparent',
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(10),
      paddingLeft: s(14),
      paddingRight: s(10),
    },
    fieldActive: {
      backgroundColor: theme.colors.surface,
      borderColor: theme.colors.primaryDark,
    },
    input: {
      flex: 1,
      minWidth: 0,
      height: '100%',
      padding: 0,
      margin: 0,
      fontFamily: GTFonts.regular,
      fontSize: fs(15),
      lineHeight: fs(20),
      color: theme.colors.text,
      ...(Platform.OS === 'android' && {
        textAlignVertical: 'center' as const,
        includeFontPadding: false,
      }),
      ...(Platform.OS === 'web' && { outlineStyle: 'none' }),
    } as any,
    inputActive: {
      fontFamily: GTFonts.semibold,
    },
    clear: {
      width: s(26),
      height: s(26),
      borderRadius: s(13),
      backgroundColor: theme.colors.surfaceSecondary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    add: {
      width: v(48),
      height: v(48),
      borderRadius: s(14),
      backgroundColor: theme.colors.primarySurface,
      alignItems: 'center',
      justifyContent: 'center',
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    } as any,
    addPressed: {
      opacity: 0.75,
    },
  });

export default PickerSearchBar;
